/**
 * App iconography. The brand mascot + bomb are Soft Clay 3D renders (Replicate,
 * background-removed) served from /brand. The small team flags stay inline SVG so
 * they render crisply in 20px board cells where a photographic sprite would muddy.
 */
import type { Team } from "@msf/shared";

const TEAM_FILL: Record<Team, { light: string; dark: string; pole: string }> = {
  red: { light: "#ff8a7a", dark: "#e0524a", pole: "#7a5a44" },
  blue: { light: "#7cc0f0", dark: "#3d84c9", pole: "#7a5a44" },
};

export function FlagIcon({ team, className }: { team: Team; className?: string }) {
  const c = TEAM_FILL[team];
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
      <rect x="8" y="4" width="3" height="24" rx="1.5" fill={c.pole} />
      <path
        d="M11 5c5-2 8 2 13 0v11c-5 2-8-2-13 0z"
        fill={c.light}
        stroke={c.dark}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <ellipse cx="9.5" cy="28" rx="5" ry="1.6" fill="rgba(0,0,0,0.12)" />
    </svg>
  );
}

export function MineIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
      <g stroke="#2c2622" strokeWidth="2.4" strokeLinecap="round">
        <line x1="16" y1="4" x2="16" y2="28" />
        <line x1="4" y1="16" x2="28" y2="16" />
        <line x1="7.5" y1="7.5" x2="24.5" y2="24.5" />
        <line x1="24.5" y1="7.5" x2="7.5" y2="24.5" />
      </g>
      <circle cx="16" cy="16" r="8" fill="#3a332e" />
      <circle cx="13" cy="13" r="2.4" fill="#e9dcc6" opacity="0.85" />
    </svg>
  );
}

/** Clay bomb render — used for the bomb power-up. */
export function BombIcon({ className }: { className?: string }) {
  return (
    <img
      src="/brand/bomb.png"
      alt=""
      aria-hidden="true"
      draggable={false}
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

/** The brand mascot: a happy clay bomb flying its flag. */
export function FlagLogo({ className }: { className?: string }) {
  return (
    <img
      src="/brand/mascot.png"
      alt="Minesweeper Flags"
      draggable={false}
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

/**
 * Decorative Soft Clay 3D renders (Replicate → background-removed → trimmed, see
 * scripts/generate-assets.mjs), used in place of emoji for standalone icons shown
 * at ≥28px. Small functional chrome (<28px or inline with text) stays crisp inline
 * SVG below instead — a photographic render would blur/misalign at that size, the
 * same reasoning that keeps the board's team flags as SVG.
 */
function BrandImg({ name, alt = "", className }: { name: string; alt?: string; className?: string }) {
  return (
    <img
      src={`/brand/${name}.png`}
      alt={alt}
      aria-hidden={alt === "" ? true : undefined}
      draggable={false}
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

export function HourglassIcon(props: { className?: string }) {
  return <BrandImg name="hourglass" {...props} />;
}
export function LockIcon(props: { className?: string; alt?: string }) {
  return <BrandImg name="lock" {...props} />;
}
export function GlobeIcon(props: { className?: string; alt?: string }) {
  return <BrandImg name="globe" {...props} />;
}
export function DiceIcon(props: { className?: string }) {
  return <BrandImg name="dice" {...props} />;
}
export function CraterIcon(props: { className?: string }) {
  return <BrandImg name="crater" {...props} />;
}
export function PartyIcon(props: { className?: string }) {
  return <BrandImg name="party" {...props} />;
}
export function SadClayIcon(props: { className?: string }) {
  return <BrandImg name="sad-clay" {...props} />;
}

/* ------------------------- small functional SVG chrome ------------------------ */
/** currentColor-based, stroke-forward — for tiny/inline UI glyphs where a raster
 * render would blur (back/close/refresh/check/theme/rematch/inline bomb prompt). */

const strokeProps = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

export function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...strokeProps}>
      <path d="M15 5l-7 7 7 7" />
    </svg>
  );
}

export function CloseIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...strokeProps}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

export function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...strokeProps}>
      <path d="M20 11a8 8 0 10-2.34 5.66M20 11V5m0 6h-6" />
    </svg>
  );
}

export function CheckIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...strokeProps}>
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

export function SunIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...strokeProps}>
      <circle cx="12" cy="12" r="4.5" />
      <path d="M12 2.5v2.2M12 19.3v2.2M4.2 4.2l1.6 1.6M18.2 18.2l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.2 19.8l1.6-1.6M18.2 5.8l1.6-1.6" />
    </svg>
  );
}

export function MoonIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...strokeProps}>
      <path d="M20 14.5A8.5 8.5 0 119.5 4 6.8 6.8 0 0020 14.5z" />
    </svg>
  );
}

export function SystemThemeIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...strokeProps}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 3.5a8.5 8.5 0 010 17z" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function ChatIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...strokeProps}>
      <path d="M4 5.5h16v10H9.5L5 19.5v-4H4z" strokeLinejoin="round" />
    </svg>
  );
}

export function RematchIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...strokeProps}>
      <path d="M4 11a8 8 0 0114-5M20 6v5h-5" />
      <path d="M20 13a8 8 0 01-14 5M4 18v-5h5" />
    </svg>
  );
}

/** Small inline bomb silhouette for the in-sentence "bomb armed" prompt — distinct
 * from the big raster BombIcon used for the standalone power-up button. */
export function BombGlyphIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        d="M13 3l1.8 1.8M13 3l-1.8 1.8M13 3v3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="11" cy="14" r="7" fill="currentColor" />
    </svg>
  );
}
